import React, { useCallback, useState, useMemo } from "react";
import { Checkbox, Tooltip, Loader, Group, Text } from "@mantine/core";

interface Props {
  isCompleted: boolean;
  canToggle: boolean;
  onToggle: (newValue: boolean, data?: any) => Promise<void>;
}

export const ReceivedCompletedCheckbox: React.FC<Props> = ({
  isCompleted,
  canToggle,
  onToggle,
}) => {
  const [loading, setLoading] = useState(false);
  const [pendingChecked, setPendingChecked] = useState<boolean | null>(null);
  const [lastAuthoritativeChecked, setLastAuthoritativeChecked] =
    useState(isCompleted);
  const [unsaved, setUnsaved] = useState(false);

  if (isCompleted !== lastAuthoritativeChecked) {
    setLastAuthoritativeChecked(isCompleted);
    if (pendingChecked === isCompleted) setPendingChecked(null);
  }

  const localChecked = useMemo(
    () => pendingChecked ?? isCompleted,
    [isCompleted, pendingChecked]
  );

  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!canToggle) return;
      const checked = event.currentTarget.checked;
      setPendingChecked(checked);
      setUnsaved(true);
      setLoading(true);

      try {
        await onToggle(checked);
        setUnsaved(false);
      } catch (err: any) {
        // Revert on failure unless it's a 409 requesting an orphan confirm
        if (err.response?.status !== 409) {
          setPendingChecked(null);
        }
      } finally {
        setLoading(false);
      }
    },
    [canToggle, onToggle]
  );

  const label = useMemo(
    () => (localChecked ? "Pre-processed" : "Unprocessed"),
    [localChecked]
  );

  const tooltip = useMemo(
    () =>
      localChecked
        ? "Slide has been pre-processed by an external facility."
        : "Slide has not been pre-processed and requires internal handling.",
    [localChecked]
  );

  const content = (
    <Group gap="xs">
      <Tooltip label={tooltip} openDelay={500} withinPortal>
        <Checkbox
          checked={localChecked}
          onChange={handleChange}
          disabled={!canToggle || loading}
          label={label}
          color="violet"
        />
      </Tooltip>
      {loading && <Loader size="xs" />}
      {unsaved && !loading && (
        <Text size="xs" c="dimmed">
          (unsaved)
        </Text>
      )}
    </Group>
  );

  if (!canToggle) {
    return (
      <Tooltip label="You lack permission to toggle this post-accessioning.">
        <div style={{ display: "inline-block", cursor: "not-allowed" }}>
          {content}
        </div>
      </Tooltip>
    );
  }

  return content;
};
